import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Which panel the right-hand drawer is showing on compact (mobile/tablet)
 * layouts. `null` means the drawer is closed. */
export type RightPane = "inspect" | "projects" | null;

/** A deferred "create" intent raised from the mobile add-sheet and consumed by
 * the graph tab (which owns the note/org dialogs). */
export type CreateIntent = "note" | "org" | null;

/** Which panel the desktop obsidian shell's RightBar is showing. `"inspect"`
 * mounts the per-node/edge InspectorPanel — auto-selected when a canvas
 * selection is made (mirrors the old RightRail behavior), same as the other
 * two panes are chosen explicitly by clicking their tab. */
export type RightBarPane = "annotations" | "pins" | "inspect";

/** Which tab the Settings overlay is showing. */
export type SettingsTab = "profile" | "appearance" | "billing";

interface ShellState {
  /** Left files drawer (mobile only). */
  leftOpen: boolean;
  /** Right context drawer (mobile only) — inspector or projects, or closed. */
  rightPane: RightPane;
  /** The mobile "+" add sheet. */
  addSheetOpen: boolean;
  /** A pending create the graph tab should act on. */
  create: CreateIntent;

  openLeft: () => void;
  closeLeft: () => void;
  toggleLeft: () => void;

  openRight: (pane: Exclude<RightPane, null>) => void;
  closeRight: () => void;
  /** Tap a dock destination: open it, or close if it is already showing. */
  toggleRight: (pane: Exclude<RightPane, null>) => void;

  openAddSheet: () => void;
  closeAddSheet: () => void;

  requestCreate: (kind: Exclude<CreateIntent, null>) => void;
  clearCreate: () => void;

  /** Dismiss every transient mobile surface (drawers + sheet). */
  closeAll: () => void;

  // ── Shared chrome collapse (compact shell + all panels) ──────────────────
  // Persisted user PREFERENCES (never transient overlays), so they survive a
  // reload the way the desktop `leftBarOpen`/`leftSwap` do.

  /** Compact `shell/TopBar` — collapsed by default to a thin pill (matches the
   * desktop `appshell/TopBar` idiom); a small `⌄` expands it to hold search/
   * add/account. Kept separate from the desktop `topOpen` (different default
   * persistence, never both mounted at once). */
  compactTopOpen: boolean;
  toggleCompactTop: () => void;

  /** Compact `MobileDock` (bottom nav) — visible by default; a small handle
   * collapses it to a slim edge pill and brings it back. */
  dockOpen: boolean;
  toggleDock: () => void;

  // ── Desktop obsidian shell layout (appshell/**) ──────────────────────────
  // Distinct names from the mobile-drawer fields above (leftOpen/rightPane):
  // those are consumed, as-is, by GraphTab/OrbitTab/MobileDock/MobileAddSheet/
  // use-router — colliding with them would break those surfaces. This is a
  // SEPARATE piece of state for the LeftBar/TopBar/RightBar desktop chrome.

  /** LeftBar (Tools/Files) open on desktop. Persisted. */
  leftBarOpen: boolean;
  /** TopBar — collapsed by default; a small `⌄` edge button expands it. */
  topOpen: boolean;
  /** RightBar — collapsed by default; a small edge button expands it. */
  rightOpen: boolean;
  /** false = Tools above Files (default); true = swapped. Persisted. */
  leftSwap: boolean;
  /** Which panel the RightBar shows when open. */
  rightBarPane: RightBarPane;

  /** Fullscreen: the opened content becomes a borderless sheet — all chrome
   * hides except the LeftBar's collapsed spine. Expanding the LeftBar while
   * fullscreen re-reveals the top-bar affordance and the RightBar spine;
   * collapsing it hides them again. Persisted on purpose (a reload STAYS in
   * fullscreen — see `partialize` below). */
  fullscreen: boolean;

  settingsOpen: boolean;
  settingsTab: SettingsTab;
  /** The keyboard map, on `?`. */
  shortcutsOpen: boolean;
  billingOpen: boolean;
  chatOpen: boolean;
  /** The "add website" dialog — global (mounted once in App.tsx) so it's
   * reachable from anywhere, including GraphTab/OrbitTab now that they live
   * nested inside Dockview panels rather than as App.tsx's direct children
   * (too deep to prop-drill an `onAddWebsite` callback through the dock). */
  websiteOpen: boolean;

  /**
   * Which shell is on screen. Set by App from the media query.
   *
   * The two layouts have two vocabularies for the same two panels — compact
   * says `toggleLeft`/`toggleRight`, desktop says `toggleLeftBar`/
   * `toggleRightBar` — and the Tools registry is shared by both. Before this,
   * the Library and Context tools called the desktop pair unconditionally, so
   * on a phone they toggled state nothing was rendering and the tile did
   * nothing. A tool should say WHAT it wants; this is how the store works out
   * where.
   */
  compact: boolean;
  setCompact: (compact: boolean) => void;
  /** Show or hide the file panel, in whichever shell is mounted. */
  toggleLibrary: () => void;
  /** Show or hide the context panel, in whichever shell is mounted. */
  toggleContext: () => void;
  /** Open the pinned/projects panel, in whichever shell is mounted. */
  openPins: () => void;

  toggleLeftBar: () => void;
  toggleTopBar: () => void;
  /** Open the top area (idempotent) — used where revealing search is part of
   * another action rather than the action itself. */
  openTopBar: () => void;
  toggleRightBar: () => void;
  toggleFullscreen: () => void;
  exitFullscreen: () => void;
  /** Open the RightBar, optionally switching to a specific pane. */
  openRightBar: (pane?: RightBarPane) => void;
  toggleLeftSwap: () => void;
  setRightBarPane: (pane: RightBarPane) => void;

  openSettings: (tab?: SettingsTab) => void;
  openShortcuts: () => void;
  closeShortcuts: () => void;
  toggleShortcuts: () => void;
  closeSettings: () => void;
  openBilling: () => void;
  closeBilling: () => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  openWebsite: () => void;
  closeWebsite: () => void;
}

export const useShell = create<ShellState>()(
  persist(
    (set, get) => ({
      leftOpen: false,
      rightPane: null,
      addSheetOpen: false,
      create: null,

      // Opening one drawer closes the other so only a single overlay is ever live.
      openLeft: () => set({ leftOpen: true, rightPane: null }),
      closeLeft: () => set({ leftOpen: false }),
      toggleLeft: () => (get().leftOpen ? set({ leftOpen: false }) : get().openLeft()),

      openRight: (pane) => set({ rightPane: pane, leftOpen: false }),
      closeRight: () => set({ rightPane: null }),
      toggleRight: (pane) =>
        get().rightPane === pane ? set({ rightPane: null }) : get().openRight(pane),

      openAddSheet: () => set({ addSheetOpen: true }),
      closeAddSheet: () => set({ addSheetOpen: false }),

      requestCreate: (kind) => set({ create: kind }),
      clearCreate: () => set({ create: null }),

      closeAll: () => set({ leftOpen: false, rightPane: null, addSheetOpen: false }),

      // ── Shared chrome collapse ─────────────────────────────────────────
      compactTopOpen: false,
      toggleCompactTop: () => set((s) => ({ compactTopOpen: !s.compactTopOpen })),
      dockOpen: true,
      toggleDock: () => set((s) => ({ dockOpen: !s.dockOpen })),

      // ── Desktop obsidian shell layout ──────────────────────────────────
      leftBarOpen: true,
      topOpen: false,
      rightOpen: false,
      leftSwap: false,
      rightBarPane: "pins",
      fullscreen: false,
      settingsOpen: false,
      settingsTab: "profile",
      shortcutsOpen: false,
      billingOpen: false,
      chatOpen: false,
      websiteOpen: false,

      compact: false,
      setCompact: (compact) => set({ compact }),
      toggleLibrary: () =>
        get().compact ? get().toggleLeft() : get().toggleLeftBar(),
      // Compact has no "collapsed rail with a remembered pane": the drawer is
      // either up or it is not, and it needs to be told which pane to show.
      // Inspect is the one a person means by "context".
      toggleContext: () =>
        get().compact ? get().toggleRight("inspect") : get().toggleRightBar(),
      openPins: () =>
        get().compact ? get().openRight("projects") : get().openRightBar("pins"),

      toggleLeftBar: () => set((s) => ({ leftBarOpen: !s.leftBarOpen })),
      toggleTopBar: () => set((s) => ({ topOpen: !s.topOpen })),
      openTopBar: () => set({ topOpen: true }),
      toggleRightBar: () => set((s) => ({ rightOpen: !s.rightOpen })),
      // Entering fullscreen collapses everything to the borderless-paper state
      // (left spine only); exiting restores nothing implicitly — the bars come
      // back at their pre-fullscreen booleans, which entering set to closed.
      toggleFullscreen: () =>
        set((s) =>
          s.fullscreen
            ? { fullscreen: false }
            : { fullscreen: true, leftBarOpen: false, topOpen: false, rightOpen: false },
        ),
      exitFullscreen: () => set({ fullscreen: false }),
      openRightBar: (pane) =>
        set((s) => ({ rightOpen: true, rightBarPane: pane ?? s.rightBarPane })),
      toggleLeftSwap: () => set((s) => ({ leftSwap: !s.leftSwap })),
      setRightBarPane: (pane) => set({ rightBarPane: pane, rightOpen: true }),

      openSettings: (tab) => set((s) => ({ settingsOpen: true, settingsTab: tab ?? s.settingsTab })),
      closeSettings: () => set({ settingsOpen: false }),
      openShortcuts: () => set({ shortcutsOpen: true }),
      closeShortcuts: () => set({ shortcutsOpen: false }),
      toggleShortcuts: () => set((s) => ({ shortcutsOpen: !s.shortcutsOpen })),
      openBilling: () => set({ billingOpen: true }),
      closeBilling: () => set({ billingOpen: false }),
      toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen })),
      openChat: () => set({ chatOpen: true }),
      closeChat: () => set({ chatOpen: false }),
      openWebsite: () => set({ websiteOpen: true }),
      closeWebsite: () => set({ websiteOpen: false }),
    }),
    {
      name: "graphmind.shell",
      // Only durable layout PREFERENCES persist — everything else (mobile
      // drawers, overlays, chat) must always start closed/fresh on load, never
      // resurrect a stale overlay on refresh/direct-load. The chrome-collapse
      // flags are preferences (default collapsed/open), so they persist too.
      partialize: (s) => ({
        leftBarOpen: s.leftBarOpen,
        leftSwap: s.leftSwap,
        compactTopOpen: s.compactTopOpen,
        dockOpen: s.dockOpen,
        // Fullscreen is a working MODE, not a transient overlay: reloading mid
        // session used to dump you back into the chrome you had deliberately
        // put away.
        fullscreen: s.fullscreen,
      }),
    },
  ),
);
